import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { httpClient } from './http-client';
import { syncCustomerHeadersFromProfile } from './CustomerContextHeaders';

let tokenRefreshInterval = null;
let isRefreshing = false;

const isTokenExpiring = (token) => {
  if (!token || !token.payload || !token.payload.exp) {
    return true;
  }
  
  const expirationTime = token.payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds buffer
  
  return expirationTime - currentTime < bufferTime;
};

const refreshToken = async () => {
  if (isRefreshing) {
    return true; 
  }
  
  try {
    isRefreshing = true;
    const session = await fetchAuthSession({ forceRefresh: true });
    
    if (session.tokens && session.tokens.accessToken) {
      const token = session.tokens.accessToken.toString();
      httpClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const profileData = localStorage.getItem("profile");
      if (profileData) {
        try {
          const cachedProfile = JSON.parse(profileData);
          cachedProfile.token = token;
          localStorage.setItem("profile", JSON.stringify(cachedProfile));
          syncCustomerHeadersFromProfile(cachedProfile);
        } catch (error) {
          console.warn("Could not update profile token:", error);
        }
      }
      
      isRefreshing = false;
      return true;
    }
    
    isRefreshing = false;
    return false;
  } catch (error) {
    console.error("Token refresh failed:", error);
    isRefreshing = false;
    
    await handleTokenExpiration();
    return false;
  }
};

const handleTokenExpiration = async () => {
  console.warn("Token expired or refresh failed - redirecting to login");
  
  stopTokenRefresh();
  
  localStorage.removeItem("profile");
  localStorage.removeItem("cognito-access-token");
  localStorage.removeItem("cognito-id-token");
  localStorage.removeItem("cognito-refresh-token");
  localStorage.removeItem("cognito-token-expiry");
  
  try {
    await signOut();
  } catch (signOutError) {
    console.error("Error signing out:", signOutError);
  }
  window.location.reload();
};

const calculateRefreshCheckTime = (token) => {
  if (!token || !token.payload || !token.payload.exp) {
    return 60000;
  }
  
  const expirationTime = token.payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;
  const bufferTime = 60 * 1000; // Check 60 seconds before expiration
  
  if (timeUntilExpiration <= bufferTime) {
    return 0;
  }
  
  return timeUntilExpiration - bufferTime;
};

const scheduleNextRefreshCheck = async () => {
  try {
    const session = await fetchAuthSession();
    
    if (!session.tokens || !session.tokens.accessToken) {
      await handleTokenExpiration();
      return;
    }
    
    const checkTime = calculateRefreshCheckTime(session.tokens.accessToken);
    
    stopTokenRefresh();
    
    tokenRefreshInterval = setTimeout(async () => {
      try {
        const currentSession = await fetchAuthSession();
        
        if (!currentSession.tokens || !currentSession.tokens.accessToken) {
          await handleTokenExpiration();
          return;
        }
        
        if (isTokenExpiring(currentSession.tokens.accessToken)) {
          console.log("Token expiring soon, refreshing...");
          const refreshed = await refreshToken();
          if (!refreshed) {
            await handleTokenExpiration();
            return;
          }
        }
        scheduleNextRefreshCheck();
      } catch (error) {
        console.error("Error checking token expiration:", error);
        await handleTokenExpiration();
      }
    }, checkTime);
    
    console.log(`Token refresh check scheduled in ${Math.round(checkTime / 1000)} seconds`);
  } catch (error) {
    console.error("Error scheduling token refresh check:", error);
    // Fallback to 60-second interval if we can't determine expiration
    stopTokenRefresh();
    tokenRefreshInterval = setTimeout(() => {
      scheduleNextRefreshCheck();
    }, 60000);
  }
};

const startTokenRefresh = () => {
  stopTokenRefresh();
  scheduleNextRefreshCheck();
};

const stopTokenRefresh = () => {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

export { 
  refreshToken, 
  handleTokenExpiration, 
  startTokenRefresh, 
  stopTokenRefresh,
  isTokenExpiring 
};


